import type { ApiService } from './apiService';
import type {
    ProductSummary,
    ArticleGraph,
    ComparableFieldDefinition,
    ProductComparerAiAnalysisRequest,
    ProductComparerAiAnalysisResponse,
    ProductComparerExcelExportRequest,
} from '../types/productComparer';

/**
 * Client service for the Product Comparer backend endpoints.
 * Uses the shared authenticated ApiService instance.
 */
export class ProductComparerApiService {
    api: ApiService;

    constructor(api: ApiService) {
        this.api = api;
    }

    /** Search for Item entities by name. */
    async searchItems(query: string): Promise<ProductSummary[]> {
        return this.api.get<ProductSummary[]>(
            `/product-comparer/search?query=${encodeURIComponent(query)}`
        );
    }

    /** Fetch the full object graph for a single Item entity. */
    async getFullItemGraph(entityId: number): Promise<ArticleGraph> {
        return this.api.get<ArticleGraph>(`/product-comparer/items/${entityId}`);
    }

    /** Compare multiple items by returning their full graphs. */
    async compareItems(entityIds: number[]): Promise<ArticleGraph[]> {
        return this.api.post<ArticleGraph[]>('/product-comparer/compare', {
            entityIds,
        });
    }

    /** Get the list of fields available for comparison. */
    async getAvailableFields(): Promise<ComparableFieldDefinition[]> {
        return this.api.get<ComparableFieldDefinition[]>('/product-comparer/fields');
    }

    /** Run AI analysis from innovation and commercial perspectives. */
    async analyzeWithAi(request: ProductComparerAiAnalysisRequest): Promise<ProductComparerAiAnalysisResponse> {
        return this.api.post<ProductComparerAiAnalysisResponse>('/product-comparer/ai-analysis', request);
    }

    /** Export product comparison report to Excel. */
    async exportToExcel(request: ProductComparerExcelExportRequest): Promise<Blob> {
        return this.api.postForBlob('/product-comparer/export-excel', request);
    }
}
